import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FolderKanban, GitBranch, ShieldCheck, Ticket } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type CaseRow = {
  case_id: string
  source_system?: string
  source_ticket_id?: string
  classification?: string
  quarantine_reason?: string | null
  latest_packet_sha256?: string
  packet_count?: number
  approval_state?: string
  handoff_decision?: string | null
  handoff_stale?: boolean
  run_id?: string | null
  outcome_status?: string | null
  outcome_stale?: boolean
  updated_at?: string
}

type Signal = { seq: number; signal_id: string; kind: string; source: string; at: string; recorded_at: string; by: string; score?: number | null; reference?: string | null }
type Verification = {
  state: 'observing' | 'verified' | 'not_verified' | 'needs_review'
  window_days: number
  observed_from: string
  window_ends_at: string
  window_restarted_at?: string
  csat_threshold: number
  csat_required: boolean
  signals: Signal[]
  finalized_at?: string | null
  finalized_by?: string | null
  reason?: string | null
  learning_eligible: boolean
  last_signal_seq?: number | null
  revoked_reason?: string | null
  revoked_at?: string | null
}

const SIGNAL_KINDS = ['customer_reply', 'customer_confirmation', 'csat', 'reopen', 'recurrence'] as const

// Linear issue identifiers already carry their team key; desk tickets are Zendesk numbers.
function ticketLabel(system?: string, ticket?: string): string {
  if (system === 'linear') return `Linear ${ticket || ''}`.trim()
  return `ZD-${ticket || ''}`
}

function sourceName(system?: string): string {
  if (system === 'linear') return 'Linear (reported issue, not investigated)'
  if (system === 'agentic-support-desk') return 'Support Desk investigation'
  return system || 'unknown'
}

type Lineage = {
  case: Record<string, unknown> & {
    case_id: string
    source_system?: string
    source_ticket_id?: string
    classification?: string
    quarantine_reason?: string | null
    approval_state?: string
    latest_packet_sha256?: string
    packets?: Array<{ sha256: string; ingested_at: string; ingest_result: string; actor: string }>
    source_claims?: Record<string, unknown>
    time?: Record<string, string | null>
    scope?: Record<string, string | null>
    jira?: { proposal_id?: string; remote?: string } | null
    engineering_run?: { run_id?: string } | null
  }
  handoff: { decision?: string; reason?: string; owner?: string; decided_by?: string; stale?: boolean; binding_id?: string } | null
  latest_packet: {
    disposition?: string
    impact?: string
    problem_statement?: string
    diagnosis?: { classification?: string; confidence?: number; statement?: string; citations?: string[] }
    missing_evidence?: string[]
    code_review_count?: number
    proposed_change_count?: number
    producer?: { module?: string }
  } | null
  jira_proposals: Array<{ proposal_id: string; status: string; summary?: string; jira_project_key?: string; remote?: string }>
  engineering_run: {
    run_id: string
    status?: string
    merge_approved?: boolean
    merge_executed?: boolean
    review_state?: string | null
    runtime_approval?: { bindingId?: string | null }
    poc_url?: string | null
    digest?: string
    link?: { handoff_binding_id?: string | null; stale?: boolean; stale_reason?: string | null; rebound_at?: string | null }
  } | null
  outcome: {
    status?: string
    summary?: string
    reviewed_by?: string
    verification_refs?: string[]
    customer_message_sent?: boolean
    stale?: boolean
    stale_reason?: string
    run_id?: string | null
    run_stale?: boolean
    binding_id?: string
    verification?: Verification | null
  } | null
  timeline: Array<{ at?: string; kind: string; by?: string; ref?: string }>
  controls: Record<string, unknown>
}

type SdlcSnap = { runs: Array<{ run_id: string; status: string; ticket_subject?: string }> }

const DECISIONS = [
  'support_only',
  'needs_more_evidence',
  'product_opportunity',
  'engineering_defect',
  'incident',
  'duplicate',
  'reject',
] as const
const OUTCOMES = ['resolved', 'unresolved', 'deferred', 'not_reproduced'] as const

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

const field = 'w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3'
const card = 'space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6'
const h2 = 'flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold'
const primary = 'w-full rounded-full bg-[var(--color-cyan)] px-4 py-3 font-semibold text-[var(--color-void)] disabled:opacity-50'
const secondary = 'w-full rounded-full border border-[var(--color-line)] px-4 py-3 font-semibold disabled:opacity-50'

function short(value?: string | null): string {
  return value ? value.slice(0, 12) : '—'
}

export function CasesPage() {
  const { user, loading } = useAuth()
  const [params] = useSearchParams()
  const linkedCase = params.get('case')
  const [rows, setRows] = useState<CaseRow[]>([])
  const [runs, setRuns] = useState<SdlcSnap['runs']>([])
  const [active, setActive] = useState<Lineage | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [decision, setDecision] = useState<string>('engineering_defect')
  const [reason, setReason] = useState('')
  const [owner, setOwner] = useState('')
  const [jiraKey, setJiraKey] = useState('')
  const [runId, setRunId] = useState('')
  const [signalKind, setSignalKind] = useState<string>('customer_confirmation')
  const [signalScore, setSignalScore] = useState<string>('5')
  const [signalRef, setSignalRef] = useState('')
  const [outcome, setOutcome] = useState<string>('resolved')
  const [outcomeSummary, setOutcomeSummary] = useState('')
  const [outcomeRefs, setOutcomeRefs] = useState('')
  const [guidanceDraft, setGuidanceDraft] = useState('')

  async function refresh(caseId?: string) {
    const [list, snap] = await Promise.all([
      api<CaseRow[]>('/api/afos360/v1/support/cases'),
      api<SdlcSnap>('/api/afos360/v1/sdlc').catch(() => ({ runs: [] })),
    ])
    setRows(list)
    setRuns(snap.runs || [])
    const target = caseId || active?.case.case_id
    if (target) {
      setActive(await api<Lineage>(`/api/afos360/v1/support/cases/${encodeURIComponent(target)}/lineage`))
    }
  }

  useEffect(() => {
    if (user?.customer_id && user?.project_id) {
      // A link from Approvals or Engineering opens that case directly.
      void refresh(linkedCase || undefined).catch((err) =>
        setError(err instanceof Error ? err.message : 'Load failed'),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id, user?.project_id, linkedCase])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function act(label: string, fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await fn()
      setMessage(label)
      resetDrafts()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed`)
    } finally {
      setBusy(false)
    }
  }

  function resetDrafts() {
    // Drafts are case-specific: never carry one ticket's evidence or run to another case.
    setReason('')
    setOwner('')
    setJiraKey('')
    setRunId('')
    setOutcomeSummary('')
    setOutcomeRefs('')
    setSignalRef('')
    setGuidanceDraft('')
  }

  async function onSelect(caseId: string) {
    setError(null)
    setMessage(null)
    if (active?.case.case_id !== caseId) resetDrafts()
    try {
      setActive(await api<Lineage>(`/api/afos360/v1/support/cases/${encodeURIComponent(caseId)}/lineage`))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed')
    }
  }

  function onDecide(e: FormEvent) {
    e.preventDefault()
    if (!active) return
    void act('Decision recorded (bound to the latest packet)', () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/triage-decisions`, {
        method: 'POST',
        body: JSON.stringify({ decision, reason, owner, packet_sha256: active.case.latest_packet_sha256 }),
      }),
    )
  }

  function onPropose(e: FormEvent) {
    e.preventDefault()
    if (!active) return
    void act('Jira proposal recorded locally (no Jira write)', () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/jira-proposals`, {
        method: 'POST',
        body: JSON.stringify({ jira_project_key: jiraKey }),
      }),
    )
  }

  function onLinkRun(e: FormEvent) {
    e.preventDefault()
    if (!active || !runId) return
    void act('Engineering run linked', () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/engineering-run`, {
        method: 'POST',
        body: JSON.stringify({ run_id: runId }),
      }),
    )
  }

  function onSignal(e: FormEvent) {
    e.preventDefault()
    if (!active) return
    const body: Record<string, unknown> = {
      outcome_binding_id: active.outcome?.binding_id,
      kind: signalKind,
      source: 'manual',
      reference: signalRef,
    }
    if (signalKind === 'csat') body.score = Number(signalScore)
    void act(`Signal ${signalKind} recorded`, () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/outcome/signals`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    )
  }

  function onFinalize() {
    if (!active) return
    void act('Observation window closed', () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/outcome/finalize`, {
        method: 'POST',
        body: JSON.stringify({ outcome_binding_id: active.outcome?.binding_id }),
      }),
    )
  }

  function onOutcome(e: FormEvent) {
    e.preventDefault()
    if (!active) return
    void act('Outcome recorded (draft guidance only; nothing sent)', () =>
      api(`/api/afos360/v1/support/cases/${encodeURIComponent(active.case.case_id)}/outcome-reviews`, {
        method: 'POST',
        body: JSON.stringify({
          status: outcome,
          summary: outcomeSummary,
          verification_refs: outcomeRefs
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          support_guidance_draft: guidanceDraft || null,
          packet_sha256: active.case.latest_packet_sha256,
          run_id: active.engineering_run?.run_id || null,
          handoff_binding_id: active.handoff?.binding_id || null,
          run_digest: active.engineering_run?.digest || null,
          supersedes_outcome_binding_id: active.outcome?.verification ? active.outcome?.binding_id || null : null,
          seen_signal_seq: active.outcome?.verification?.last_signal_seq ?? null,
        }),
      }),
    )
  }

  const c = active?.case
  const claims = (c?.source_claims || {}) as Record<string, unknown>

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-3">
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3">
          <h1 className="afos-page-title">Case workspace</h1>
          <p className="afos-page-lead">
            One case across modules: the Support Desk packet, the named-human handoff decision, the Jira
            proposal, the engineering run and its approvals, and the independently reviewed outcome. Nothing
            here posts to Zendesk, Jira, Git, or a customer.
          </p>
          {message && <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
        </motion.section>

        <section className={card}>
          <h2 className={h2}>
            <Ticket className="h-5 w-5 text-[var(--color-amber)]" />
            Cases
          </h2>
          <div className="space-y-2 text-sm">
            {rows.length === 0 && (
              <p className="text-[var(--color-mist)]">
                No cases yet. Ingest a desk packet with <code>python -m afos360 support-intake</code>.
              </p>
            )}
            {rows.map((row) => (
              <button
                key={row.case_id}
                type="button"
                onClick={() => void onSelect(row.case_id)}
                className={`w-full rounded-2xl border px-3 py-2 text-left ${
                  active?.case.case_id === row.case_id
                    ? 'border-[var(--color-cyan)]'
                    : 'border-[var(--color-line)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{ticketLabel(row.source_system, row.source_ticket_id)}</span>
                  <span className="text-[var(--color-mist)]">{row.classification}</span>
                </div>
                <div className="text-xs text-[var(--color-mist)]">
                  {row.approval_state}
                  {row.handoff_decision ? ` · ${row.handoff_decision}${row.handoff_stale ? ' (stale)' : ''}` : ''}
                  {row.outcome_status ? ` · outcome ${row.outcome_status}${row.outcome_stale ? ' (stale)' : ''}` : ''}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className={`${card} lg:col-span-2`}>
          <h2 className={h2}>
            <FolderKanban className="h-5 w-5 text-[var(--color-cyan)]" />
            {c ? `Case ${c.case_id}` : 'Select a case'}
          </h2>
          {c && (
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="font-semibold">Packet (source claims, unverified)</div>
                <div>Producer: {active?.latest_packet?.producer?.module || '—'}</div>
                <div>Digest: {short(c.latest_packet_sha256)} · versions: {c.packets?.length || 0}</div>
                <div>Disposition: {active?.latest_packet?.disposition || '—'}</div>
                <div>Impact: {active?.latest_packet?.impact || '—'}</div>
                <div>Problem: {active?.latest_packet?.problem_statement || '—'}</div>
                <div>
                  Diagnosis: {active?.latest_packet?.diagnosis?.classification || 'unknown'} (
                  {active?.latest_packet?.diagnosis?.confidence ?? '—'}% claimed) · verification{' '}
                  {String(claims.verification_status || 'unverified')}
                </div>
                <div>
                  Code refs: {active?.latest_packet?.code_review_count ?? 0} · proposed changes:{' '}
                  {active?.latest_packet?.proposed_change_count ?? 0} (never applied)
                </div>
                <div>Missing evidence: {(active?.latest_packet?.missing_evidence || []).join('; ') || 'none'}</div>
              </div>
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="font-semibold">Scope, time, controls</div>
                <div>Source: {sourceName(c.source_system)} · {ticketLabel(c.source_system, c.source_ticket_id)}</div>
                <div>Classification: {c.classification}{c.quarantine_reason ? ` (${c.quarantine_reason})` : ''}</div>
                <div>Environment: {c.scope?.environment_id || 'unresolved'} · account: {c.scope?.source_account_id || '—'}</div>
                <div>collected {c.time?.collected_at || '—'}</div>
                <div>ingested {c.time?.ingested_at || '—'}</div>
                <div>verified {c.time?.verified_at || 'not yet'}</div>
                <div>Approval state: {c.approval_state}</div>
                <div>
                  Customer message sent: {String(active?.controls.customer_message_sent)} · Jira remote:{' '}
                  {String(active?.controls.jira_remote)} · merge executed by AFOS:{' '}
                  {String(active?.controls.merge_executed_by_afos)}
                </div>
              </div>
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="font-semibold">Decision</div>
                {active?.handoff ? (
                  <>
                    <div>
                      {active.handoff.decision}
                      {active.handoff.stale ? ' (stale — packet revised)' : ''} · owner {active.handoff.owner}
                    </div>
                    <div>by {active.handoff.decided_by} · binding {short(active.handoff.binding_id)}</div>
                    <div className="text-[var(--color-mist)]">{active.handoff.reason}</div>
                  </>
                ) : (
                  <div className="text-[var(--color-mist)]">No decision yet.</div>
                )}
              </div>
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="font-semibold">Jira · run · outcome</div>
                <div>
                  Jira:{' '}
                  {active?.jira_proposals[0]
                    ? `${active.jira_proposals[0].jira_project_key} ${active.jira_proposals[0].status} (${active.jira_proposals[0].remote})`
                    : 'no proposal'}
                </div>
                <div>
                  Run:{' '}
                  {active?.engineering_run
                    ? `${active.engineering_run.run_id} · ${active.engineering_run.status} · merge ${
                        active.engineering_run.merge_approved ? 'approved' : 'not approved'
                      } · executed ${String(active.engineering_run.merge_executed)} · binding ${short(
                        active.engineering_run.runtime_approval?.bindingId,
                      )}${
                        active.engineering_run.link?.stale
                          ? ` · LINK STALE (${active.engineering_run.link.stale_reason}) — relink under the current decision`
                          : ''
                      }`
                    : 'not linked'}
                </div>
                <div>
                  Outcome:{' '}
                  {active?.outcome
                    ? `${active.outcome.status}${active.outcome.stale ? ` (stale — ${active.outcome.stale_reason})` : ''}${active.outcome.run_stale ? ' (run changed since review)' : ''} by ${
                        active.outcome.reviewed_by
                      } · refs ${active.outcome.verification_refs?.length ?? 0} · run ${active.outcome.run_id || '—'}`
                    : 'not reviewed'}
                </div>
                {active?.outcome?.verification && (
                  <div className="space-y-1 text-xs">
                    <div>
                      Verification: <span className="font-semibold">{active.outcome.verification.state}</span>
                      {active.outcome.verification.state === 'observing'
                        ? ` · window ${active.outcome.verification.window_days}d ends ${active.outcome.verification.window_ends_at}${
                            active.outcome.verification.window_restarted_at ? ' (restarted after customer reply)' : ''
                          }`
                        : ` · ${active.outcome.verification.reason} by ${active.outcome.verification.finalized_by} at ${active.outcome.verification.finalized_at}`}
                      {` · CSAT ≥ ${active.outcome.verification.csat_threshold}${active.outcome.verification.csat_required ? ' required' : ''}`}
                      {` · learning ${active.outcome.verification.learning_eligible ? 'eligible' : 'not eligible'}`}
                      {active.outcome.verification.revoked_reason ? ` · REVOKED (${active.outcome.verification.revoked_reason}) — review again` : ''}
                    </div>
                    {active.outcome.verification.signals.length > 0 && (
                      <ul className="list-disc pl-4 text-[var(--color-mist)]">
                        {active.outcome.verification.signals.map((sig) => (
                          <li key={sig.signal_id}>
                            #{sig.seq} {sig.recorded_at} · {sig.kind}
                            {sig.kind === 'csat' ? ` ${sig.score}` : ''} · {sig.source} · {sig.by}
                            {sig.reference ? ` · ${sig.reference}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {c && (
            <div className="grid gap-4 md:grid-cols-2">
              <form className="space-y-2" onSubmit={onDecide}>
                <div className="font-semibold">Record handoff decision</div>
                <select className={field} value={decision} onChange={(e) => setDecision(e.target.value)}>
                  {DECISIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <input className={field} placeholder="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                <input className={field} placeholder="owner email" value={owner} onChange={(e) => setOwner(e.target.value)} />
                <button type="submit" disabled={busy || !reason || !owner} className={primary}>
                  Decide (binds to packet {short(c.latest_packet_sha256)})
                </button>
              </form>
              <form className="space-y-2" onSubmit={onPropose}>
                <div className="font-semibold">Propose Jira work (local, no write)</div>
                <input className={field} placeholder="Jira project key, e.g. SUP" value={jiraKey} onChange={(e) => setJiraKey(e.target.value)} />
                <button type="submit" disabled={busy || !jiraKey} className={secondary}>
                  Record proposal
                </button>
              </form>
              <form className="space-y-2" onSubmit={onLinkRun}>
                <div className="flex items-center gap-2 font-semibold">
                  <GitBranch className="h-4 w-4" /> Link engineering run
                </div>
                <select className={field} value={runId} onChange={(e) => setRunId(e.target.value)}>
                  <option value="">choose SDL_ run</option>
                  {runs.map((r) => (
                    <option key={r.run_id} value={r.run_id}>
                      {r.run_id} · {r.status}
                    </option>
                  ))}
                </select>
                <button type="submit" disabled={busy || !runId} className={secondary}>
                  Link run
                </button>
              </form>
              {active?.outcome?.verification && !active.outcome.stale && (
                <form className="space-y-2" onSubmit={onSignal}>
                  <div className="font-semibold">Observation signal (append-only)</div>
                  <select className={field} value={signalKind} onChange={(e) => setSignalKind(e.target.value)}>
                    {SIGNAL_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                  {signalKind === 'csat' && (
                    <select className={field} value={signalScore} onChange={(e) => setSignalScore(e.target.value)}>
                      {['1', '2', '3', '4', '5'].map((v) => (
                        <option key={v} value={v}>
                          CSAT {v}
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    className={field}
                    placeholder={signalKind === 'customer_reply' ? 'reference (optional)' : 'evidence reference (required)'}
                    value={signalRef}
                    onChange={(e) => setSignalRef(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button type="submit" disabled={busy || (signalKind !== 'customer_reply' && signalRef.trim().length < 3)} className={secondary}>
                      Record signal
                    </button>
                    <button type="button" disabled={busy || active.outcome.verification.state !== 'observing'} onClick={onFinalize} className={primary}>
                      Finalize (after {active.outcome.verification.window_ends_at})
                    </button>
                  </div>
                </form>
              )}
              <form className="space-y-2" onSubmit={onOutcome}>
                <div className="flex items-center gap-2 font-semibold">
                  <ShieldCheck className="h-4 w-4" /> Record outcome (independent reviewer)
                </div>
                <select className={field} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                  {OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <input className={field} placeholder="summary" value={outcomeSummary} onChange={(e) => setOutcomeSummary(e.target.value)} />
                <textarea className={field} placeholder="verification refs, one per line (required for resolved)" value={outcomeRefs} onChange={(e) => setOutcomeRefs(e.target.value)} />
                <textarea className={field} placeholder="support guidance draft (never sent)" value={guidanceDraft} onChange={(e) => setGuidanceDraft(e.target.value)} />
                <button type="submit" disabled={busy || !outcomeSummary} className={primary}>
                  Record outcome
                </button>
              </form>
            </div>
          )}
          {active && active.timeline.length > 0 && (
            <ol className="space-y-1 text-xs text-[var(--color-mist)]">
              {active.timeline.map((row, idx) => (
                <li key={`${row.kind}-${idx}`}>
                  {row.at || '—'} · {row.kind}
                  {row.by ? ` · ${row.by}` : ''}
                  {row.ref ? ` · ${short(row.ref)}` : ''}
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </AppChrome>
  )
}
